import { useEffect, useState } from 'react';
import usePreviousValue from './usePreviousValue';

const useLocalStorage = <T>(key: string, initialValue: T): readonly [T, (newVal: T | ((prevVal: T) => T)) => void] => {
  const previousKey = usePreviousValue(key);

  const getInitialValue = () => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      return initialValue;
    }
  };

  const [storedValue, setStoredValue] = useState<T>(getInitialValue);

  const setValue = (value: T | ((val: T) => T)) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
      // eslint-disable-next-line no-empty
    } catch (error) {}
  };

  const syncValue = ({ key: changedKey, newValue }: StorageEvent) => {
    if (changedKey === key && newValue) {
      try {
        setStoredValue(JSON.parse(newValue));
        // eslint-disable-next-line no-empty
      } catch (error) {}
    }
  };

  useEffect(() => {
    if (key !== previousKey) {
      const value = getInitialValue();
      setStoredValue(value);
    }
  }, [key]);

  useEffect(() => {
    window.addEventListener('storage', syncValue);
    return () => {
      window.removeEventListener('storage', syncValue);
    };
  }, []);

  return [storedValue, setValue] as const;
};

export default useLocalStorage;
